import { ArrowForwardIosSharp } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Grid, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ControlledTextEditor from 'src/shared/components/form/controlledTextEditor';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { FundraiserSettingsAccordionEnum, FundraiserSettingsSectionPrefixEnum } from 'src/shared/models/enums/fundraiserSettingsAccordion.enum';
import { GetFilteredAccordionContent } from './fundraiserSettingsAccordionContent';
import { FundraiserSysSettings } from '../models/fundraiserSysSettings';

type FundraiserSettingsAccordionProps = {
  prefix: FundraiserSettingsSectionPrefixEnum;
  excludeSection?: FundraiserSettingsAccordionEnum[];
};

// Datafält för givar-mailen (E-postkvitto + Gåvobrev). Enbart de platshållare som faktiskt fylls vid utskicket:
// worker-servicen kör ReplaceFundraiserProperties (fyller [fund.*]/[deposit.*]) men ReplaceMemberProperties med en
// TOM medlem – givaren är inte medlem, så [member.*] skulle bli tomt och tas därför inte med här. Listan är avsiktligt
// begränsad och gäller ENBART dessa två editorer (via customFields-propen); övriga editorer i systemet är opåverkade.
const donorEmailDataFields = [
  { label: 'Givarens namn', value: '[deposit.name]' },
  { label: 'Belopp', value: '[deposit.amount]' },
  { label: 'Givarens hälsning', value: '[deposit.message]' },
  { label: 'Ytterligare information', value: '[deposit.orderData]' },
  { label: 'Gatuadress', value: '[deposit.address1]' },
  { label: 'Postnummer', value: '[deposit.postalCode]' },
  { label: 'Ort', value: '[deposit.city]' },
  { label: 'Givarens e-post', value: '[deposit.emailAddress]' },
  { label: 'Insamlingens rubrik', value: '[fund.heading]' },
];

export const FundraiserSettingsAccordion = (props: FundraiserSettingsAccordionProps) => {
  const { prefix, excludeSection } = props;
  const { t } = useTranslation();

  const { watch } = useFormContext<FundraiserSysSettings>();
  const currentValues = watch(prefix);
  const accordionSections = GetFilteredAccordionContent(excludeSection);

  return (
    <Box
      sx={{
        '& .MuiAccordionSummary-root': {
          flexDirection: 'row-reverse',
          '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
            transform: 'rotate(90deg)',
          },
          '& .MuiAccordionSummary-content': {
            ml: 1,
          },
        },
        '& .MuiAccordion-root.Mui-expanded': {
          borderBottom: '1px solid #e0e0e0',
          boxShadow: '0 20px 15px -20px rgba(0, 0, 0, 0.1) inset !important',
          margin: 0,
        },
      }}
    >
      <Box sx={{ maxHeight: '60vh', overflowY: 'auto', pr: 1 }}>
        {accordionSections.map((section) => {
          const panelId = `fundraiser-settings-panel-${section.id}`;
          const contentId = `fundraiser-settings-content-${section.id}`;

          return (
            <Accordion key={section.id} disableGutters elevation={0}>
              <AccordionSummary expandIcon={<ArrowForwardIosSharp sx={{ fontSize: '0.9rem' }} />} aria-controls={contentId} id={panelId}>
                <Typography sx={{ fontWeight: 500 }} mr={0.5}>
                  {t(section.label)}
                </Typography>
              </AccordionSummary>

              <AccordionDetails id={contentId}>
                <Box mb={3} sx={{ marginLeft: { xs: 0, sm: 5 }, marginRight: { xs: 0, sm: 5 } }}>
                  {section.id === FundraiserSettingsAccordionEnum.NewDonationNotice && currentValues.newDonationNotice && (
                    <>
                      <Grid item xs={12} mt={2} mb={3}>
                        <ControlledTextField name={`${prefix}.newDonationNotice.subject`} label={t('common:header')} />
                      </Grid>
                      <Grid item xs={12} mb={2}>
                        <ControlledTextEditor name={`${prefix}.newDonationNotice.body`} />
                      </Grid>
                    </>
                  )}

                  {section.id === FundraiserSettingsAccordionEnum.PostPaymentText && currentValues.postPaymentText && (
                    <>
                      <Grid item xs={12} mt={2} mb={3}>
                        <ControlledTextField name={`${prefix}.postPaymentText.heading`} label={t('common:header')} />
                      </Grid>
                      <Grid item xs={12} mb={3}>
                        <ControlledTextEditor name={`${prefix}.postPaymentText.ingress`} />
                      </Grid>
                      <Grid item xs={12} mb={3}>
                        <ControlledTextField name={`${prefix}.postPaymentText.returnUrl`} label={t('fundraiser:returnUrl')} />
                      </Grid>
                    </>
                  )}

                  {section.id === FundraiserSettingsAccordionEnum.ReceiptToDonor && currentValues.receiptToDonor && (
                    <>
                      <Grid item xs={12} mt={2} mb={3}>
                        <ControlledTextField name={`${prefix}.receiptToDonor.subject`} label={t('common:header')} />
                      </Grid>
                      <Grid item xs={12} mb={2}>
                        <ControlledTextEditor name={`${prefix}.receiptToDonor.body`} customFields={donorEmailDataFields} />
                      </Grid>
                    </>
                  )}

                  {section.id === FundraiserSettingsAccordionEnum.GiftLetterToDonor && currentValues.giftLetterToDonor && (
                    <>
                      <Grid item xs={12} mt={2} mb={3}>
                        <ControlledTextField name={`${prefix}.giftLetterToDonor.subject`} label={t('common:header')} />
                      </Grid>
                      <Grid item xs={12} mb={2}>
                        {/* Datafälts-menyn döljs: gåvobrevets utskicksväg (SendAttachment i worker services) kör ingen
                            token-ersättning, så inga [deposit.*]/[fund.*]/[prop.*] fungerar här. Vi erbjuder därför inga. */}
                        <ControlledTextEditor name={`${prefix}.giftLetterToDonor.body`} hidedataPropFields />
                      </Grid>
                    </>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    </Box>
  );
};
